import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import preParsePostFormat from "dayjs/plugin/preParsePostFormat";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import enUS from "antd/locale/en_US";
import arEG from "antd/locale/ar_EG";
import type { Locale } from "antd/es/locale";
import { SUPPORTED_LANGS, type Language } from "src/store/slices/common/common.enum";

dayjs.extend(preParsePostFormat);

export type Direction = "rtl" | "ltr";

export const RTL_LANGUAGES = ["ar"];

export const getDirection = (language: string): Direction => (RTL_LANGUAGES.includes(language) ? "rtl" : "ltr");

export const isRtl = (language: string): boolean => getDirection(language) === "rtl";

export const toLanguage = (language: string): Language =>
  Object.values(SUPPORTED_LANGS).find((lang) => lang === language) ?? SUPPORTED_LANGS.english;

const DAYJS_LOCALES: Record<string, string> = { ar: "ar", en: "en" };

const arEGWithShortWeekDays: Locale = {
  ...arEG,
  DatePicker: {
    ...arEG.DatePicker!,
    lang: {
      ...arEG.DatePicker!.lang,
      shortWeekDays: undefined,
    },
  },
};

const ANTD_LOCALES: Record<string, Locale> = { ar: arEGWithShortWeekDays, en: enUS };

export const getDayjsLocale = (language: string): string => DAYJS_LOCALES[language] ?? DAYJS_LOCALES.en;

export const getAntdLocale = (language: string): Locale => ANTD_LOCALES[language] ?? ANTD_LOCALES.en;

export function useDirection(): Direction {
  const { i18n } = useTranslation();
  return getDirection(i18n.language);
}

export function useDateLocale() {
  const { i18n } = useTranslation();
  dayjs.locale(getDayjsLocale(i18n.language));
  return getAntdLocale(i18n.language);
}

export function useMirrorTransform(baseTransform?: string): string | undefined {
  const direction = useDirection();
  const mirror = direction === "rtl" ? "scale(-1,1)" : "";
  const combined = [baseTransform, mirror].filter(Boolean).join(" ");
  return combined;
}
